/**
 * Auth state management — Google OAuth + JWT tokens
 */

import { AUTH_API, GOOGLE_CLIENT_ID, MICROSOFT_LOGIN_ENABLED, PORTAL_WEB_ORIGIN } from './config'

export interface AuthUser {
  id: string
  email: string
  first_name: string
  last_name: string
  is_superadmin: boolean
  roles: Array<{ sys: string; role: string; tid: string | null }>
}

interface AuthState {
  user: AuthUser | null
  accessToken: string | null
  refreshToken: string | null
}

const STORAGE_KEY = 'content_auth'

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Eingebetteter Editor (/frame/post/:id, #2574, Post 5623)
//
// Im Portal eingebettet nimmt die content-app ihre Anmeldung nur vom
// Portal (postMessage `embed-auth`, siehe FrameView) und hält sie im
// Speicher. Die eigene Anmeldung im localStorage wird dort nie gelesen
// und nie geschrieben: Eine fremde Seite, die die Route einbettet, bekommt
// so weder die Sitzung des Browsers noch Schreibrecht. Erneuert wird über
// das Portal statt über den Refresh-Token.
// ---------------------------------------------------------------------------

export const EMBEDDED_FRAME =
  typeof window !== 'undefined' && window.location.pathname.startsWith('/frame/')

type EmbeddedTokenRequester = () => Promise<string | null>
let embeddedTokenRequester: EmbeddedTokenRequester | null = null

/** FrameView meldet hier, wie ein frisches Token beim Portal angefragt wird. */
export function setEmbeddedTokenRequester(fn: EmbeddedTokenRequester | null) {
  embeddedTokenRequester = fn
}

/** Anmeldung aus dem Portal übernehmen, nur im Speicher. */
export function setEmbeddedSession(accessToken: string, user: AuthUser) {
  if (!EMBEDDED_FRAME) return
  state = { user, accessToken, refreshToken: null }
  notify()
}

let state: AuthState = EMBEDDED_FRAME
  ? { user: null, accessToken: null, refreshToken: null }
  : load()

function load(): AuthState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (raw) return JSON.parse(raw)
  } catch { /* ignore */ }
  return { user: null, accessToken: null, refreshToken: null }
}

function persist() {
  if (EMBEDDED_FRAME) return
  localStorage.setItem(STORAGE_KEY, JSON.stringify(state))
}

// Listeners for React re-renders
type Listener = () => void
const listeners = new Set<Listener>()

export function subscribe(fn: Listener) {
  listeners.add(fn)
  return () => listeners.delete(fn)
}

function notify() {
  listeners.forEach(fn => fn())
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export function getUser(): AuthUser | null {
  return state.user
}

export function getAccessToken(): string | null {
  return state.accessToken
}

export function isLoggedIn(): boolean {
  return !!state.accessToken && !!state.user
}

export function logout() {
  if (EMBEDDED_FRAME) {
    state = { user: null, accessToken: null, refreshToken: null }
    notify()
    return
  }
  if (state.refreshToken) {
    fetch(`${AUTH_API}/api/v1/auth/logout`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: state.refreshToken }),
    }).catch(() => {})
  }
  state = { user: null, accessToken: null, refreshToken: null }
  localStorage.removeItem(STORAGE_KEY)
  notify()
}

// ---------------------------------------------------------------------------
// Google OAuth flow
// ---------------------------------------------------------------------------

/**
 * E-mail/password login against auth-api (POST /api/v1/auth/login). The
 * default path on tenant instances without a Google OAuth client; lands
 * the same JWT pair + user shape as the Google callback.
 */
export async function loginEmail(email: string, password: string): Promise<void> {
  const resp = await fetch(`${AUTH_API}/api/v1/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  })
  if (!resp.ok) {
    let detail = ''
    try { detail = (await resp.json())?.detail || '' } catch { /* text below */ }
    if (!detail) detail = await resp.text().catch(() => '')
    if (resp.status === 401 || resp.status === 403) {
      throw new Error('E-Mail oder Passwort ist falsch.')
    }
    throw new Error(typeof detail === 'string' && detail ? detail : `Login fehlgeschlagen (HTTP ${resp.status})`)
  }
  const data = await resp.json()
  state = {
    user: data.user,
    accessToken: data.access_token,
    refreshToken: data.refresh_token,
  }
  persist()
  notify()
}

export function startGoogleLogin() {
  if (!GOOGLE_CLIENT_ID) {
    throw new Error('Google-Login ist auf dieser Instanz nicht konfiguriert (VITE_GOOGLE_CLIENT_ID).')
  }
  const redirectUri = `${window.location.origin}/auth/google/callback`
  const params = new URLSearchParams({
    client_id: GOOGLE_CLIENT_ID,
    redirect_uri: redirectUri,
    response_type: 'code',
    scope: 'openid email profile',
    access_type: 'offline',
    prompt: 'select_account',
  })
  window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?${params}`
}

export async function handleGoogleCallback(code: string): Promise<void> {
  const redirectUri = `${window.location.origin}/auth/google/callback`

  const resp = await fetch(`${AUTH_API}/api/v1/auth/google/callback`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ code, redirect_uri: redirectUri }),
  })

  if (!resp.ok) {
    const err = await resp.text()
    throw new Error(err)
  }

  const data = await resp.json()
  state = {
    user: data.user,
    accessToken: data.access_token,
    refreshToken: data.refresh_token,
  }
  persist()
  notify()
}

// ---------------------------------------------------------------------------
// Anmeldewege der Instanz (GET /api/v1/auth/version)
// ---------------------------------------------------------------------------

export interface AuthCapabilities {
  /** OAuth-Anbieter mit eingerichtetem Client, z. B. ['google', 'microsoft']. */
  providers: string[]
  /** false, wenn PASSWORD_LOGIN_ENABLED=false auf der auth-api steht. */
  passwordLogin: boolean
}

let capabilitiesCache: AuthCapabilities | null = null

/**
 * Welche Anmeldewege die auth-api dieser Instanz anbietet. Die Anmeldeseite
 * blendet danach Knöpfe und Passwort-Formular ein oder aus. Antwortet die
 * auth-api nicht (Netz, 5xx), kommt `null` zurück und die Seite zeigt den
 * bisherigen Stand (Passwort-Formular), statt gar nichts anzubieten.
 */
export async function fetchAuthCapabilities(): Promise<AuthCapabilities | null> {
  if (capabilitiesCache) return capabilitiesCache
  const ctrl = new AbortController()
  const timer = setTimeout(() => ctrl.abort(), 4000)
  try {
    const resp = await fetch(`${AUTH_API}/api/v1/auth/version`, { signal: ctrl.signal })
    if (!resp.ok) return null
    const data = await resp.json()
    const providers = Array.isArray(data?.auth_providers)
      ? (data.auth_providers as unknown[]).filter((p): p is string => typeof p === 'string')
      : []
    capabilitiesCache = { providers, passwordLogin: data?.password_login !== false }
    return capabilitiesCache
  } catch {
    return null
  } finally {
    clearTimeout(timer)
  }
}

// ---------------------------------------------------------------------------
// Microsoft über den Rücksprung des Portals (Post #5420, Weg B)
//
// Content hat bei Microsoft keinen eigenen Rücksprung eingetragen. Deshalb
// startet Content die Anmeldung mit `<PORTAL>/auth/microsoft/callback`; das
// Portal erkennt am Präfix `content.` im `state`, dass der Code nicht ihm
// gehört, und reicht `code` und `state` unverändert an
// `<Content>/auth/microsoft/callback` weiter. Erst dort wird eingelöst, mit
// derselben redirect_uri wie beim Start. Der Zufallswert im `state` liegt
// im sessionStorage und bindet die Rückkehr an diesen Browser-Tab.
// ---------------------------------------------------------------------------

const MS_STATE_PREFIX = 'content.'
const MS_NONCE_KEY = 'content_ms_login_nonce'

function base64UrlEncode(input: string | Uint8Array): string {
  const bytes = typeof input === 'string' ? new TextEncoder().encode(input) : input
  let bin = ''
  bytes.forEach((b) => { bin += String.fromCharCode(b) })
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

function base64UrlDecode(input: string): string {
  const b64 = input.replace(/-/g, '+').replace(/_/g, '/')
  const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4)
  const bin = atob(padded)
  const bytes = Uint8Array.from(bin, (c) => c.charCodeAt(0))
  return new TextDecoder().decode(bytes)
}

/** Nur relative Pfade innerhalb der App; alles andere fällt auf '/'. */
export function safeNextPath(raw: string | null | undefined): string {
  const v = (raw || '').trim()
  return v.startsWith('/') && !v.startsWith('//') && !v.startsWith('/\\') ? v : '/'
}

/** Der Rücksprung, den Microsoft kennt: der des Portals, nicht unserer. */
export function microsoftRedirectUri(): string {
  return `${PORTAL_WEB_ORIGIN}/auth/microsoft/callback`
}

export async function startMicrosoftLogin(next: string = '/'): Promise<void> {
  if (!MICROSOFT_LOGIN_ENABLED) {
    throw new Error('Microsoft-Anmeldung ist auf dieser Instanz nicht konfiguriert (VITE_PORTAL_WEB_ORIGIN).')
  }
  const nonceBytes = new Uint8Array(16)
  crypto.getRandomValues(nonceBytes)
  const nonce = base64UrlEncode(nonceBytes)
  sessionStorage.setItem(MS_NONCE_KEY, nonce)
  const state = MS_STATE_PREFIX + base64UrlEncode(JSON.stringify({ n: nonce, next: safeNextPath(next) }))

  const qs = new URLSearchParams({ redirect_uri: microsoftRedirectUri() })
  const resp = await fetch(`${AUTH_API}/api/v1/auth/microsoft/authorize?${qs}`)
  if (resp.status === 503) {
    throw new Error('Microsoft-Anmeldung ist auf dieser Instanz nicht eingerichtet.')
  }
  if (resp.status === 400) {
    throw new Error('Der Rücksprung des Portals ist bei der Anmeldung nicht freigegeben (MICROSOFT_ALLOWED_REDIRECT_URIS).')
  }
  if (!resp.ok) {
    throw new Error(`Microsoft-Anmeldung konnte nicht gestartet werden (HTTP ${resp.status}).`)
  }
  const data = await resp.json()
  const url = typeof data?.url === 'string' ? data.url : ''
  if (!/^https?:\/\//.test(url)) {
    throw new Error('Die auth-api hat keine gültige Microsoft-Adresse geliefert.')
  }
  // Die auth-api setzt selbst keinen state; wir hängen unseren an.
  window.location.assign(`${url}${url.includes('?') ? '&' : '?'}state=${encodeURIComponent(state)}`)
}

export interface MicrosoftReturn {
  next: string
}

/**
 * Prüft den zurückgekommenen `state` gegen den Zufallswert dieses Tabs und
 * verbraucht ihn. `null`, wenn er fehlt, nicht von uns stammt oder nicht
 * zu diesem Tab gehört; dann wird der Code nicht eingelöst.
 */
export function consumeMicrosoftState(raw: string | null): MicrosoftReturn | null {
  if (!raw || !raw.startsWith(MS_STATE_PREFIX)) return null
  let parsed: { n?: unknown; next?: unknown }
  try {
    parsed = JSON.parse(base64UrlDecode(raw.slice(MS_STATE_PREFIX.length)))
  } catch {
    return null
  }
  const expected = sessionStorage.getItem(MS_NONCE_KEY)
  sessionStorage.removeItem(MS_NONCE_KEY)
  if (!expected || typeof parsed.n !== 'string' || parsed.n !== expected) return null
  return { next: safeNextPath(typeof parsed.next === 'string' ? parsed.next : '/') }
}

export async function handleMicrosoftCallback(code: string): Promise<void> {
  const resp = await fetch(`${AUTH_API}/api/v1/auth/microsoft/callback`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ code, redirect_uri: microsoftRedirectUri() }),
  })
  if (!resp.ok) {
    let detail = ''
    try { detail = (await resp.json())?.detail || '' } catch { /* text below */ }
    if (!detail) detail = await resp.text().catch(() => '')
    if (resp.status === 401) throw new Error('Die Anmeldung bei Microsoft wurde nicht angenommen.')
    if (resp.status === 429) throw new Error('Zu viele Anmeldeversuche, bitte in ein paar Minuten erneut.')
    throw new Error(typeof detail === 'string' && detail ? detail : `Anmeldung fehlgeschlagen (HTTP ${resp.status})`)
  }
  const data = await resp.json()
  state = {
    user: data.user,
    accessToken: data.access_token,
    refreshToken: data.refresh_token,
  }
  persist()
  notify()
}

// ---------------------------------------------------------------------------
// Phase 2 (Self-Signup) — new public endpoints (default-gated by
// auth-api's ALLOW_SELF_SIGNUP env flag; 503 until flipped). Each call
// lands a JWT-pair + user shape just like the existing Google-callback
// path so React can re-render straight from `state`.
// ---------------------------------------------------------------------------

export interface SignupEmailRequest {
  email: string
  password: string
  first_name?: string
  last_name?: string
  next_url?: string
}

export interface SignupResponse {
  user: AuthUser
  access_token: string
  refresh_token: string
  tenant_id?: string
  verification?: {
    mail_sent: boolean
    verify_url?: string
  }
}

export class SignupGatedError extends Error {
  // 503 = ALLOW_SELF_SIGNUP=false on the auth-api .env, instance is not
  // open to public registration yet.
  constructor() {
    super('Signup is not enabled yet — please contact the administrator.')
    this.name = 'SignupGatedError'
  }
}

export async function signupEmail(req: SignupEmailRequest): Promise<SignupResponse> {
  const resp = await fetch(`${AUTH_API}/api/v1/auth/signup/email`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(req),
  })
  if (resp.status === 503) throw new SignupGatedError()
  if (!resp.ok) {
    const txt = await resp.text()
    throw new Error(`Signup failed (${resp.status}): ${txt.slice(0, 200)}`)
  }
  const data = (await resp.json()) as SignupResponse
  state = {
    user: data.user,
    accessToken: data.access_token,
    refreshToken: data.refresh_token,
  }
  persist()
  notify()
  return data
}

export function startGoogleSignup(nextUrl?: string) {
  if (!GOOGLE_CLIENT_ID) {
    throw new Error('Google-Signup ist auf dieser Instanz nicht konfiguriert (VITE_GOOGLE_CLIENT_ID).')
  }
  // Mirrors startGoogleLogin but routes through /signup/google so the
  // self-signup-bypass kicks in for unknown emails.
  const redirectUri = `${window.location.origin}/auth/google/callback`
  // State carries the mode + next-url so the callback knows to call
  // /auth/signup/google (vs /auth/google/callback for plain login).
  const state_payload = JSON.stringify({ mode: 'signup', next: nextUrl || '/' })
  const params = new URLSearchParams({
    client_id: GOOGLE_CLIENT_ID,
    redirect_uri: redirectUri,
    response_type: 'code',
    scope: 'openid email profile',
    access_type: 'offline',
    prompt: 'select_account',
    state: btoa(state_payload),
  })
  window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?${params}`
}

export async function handleGoogleSignupCallback(code: string): Promise<SignupResponse> {
  const redirectUri = `${window.location.origin}/auth/google/callback`
  const resp = await fetch(`${AUTH_API}/api/v1/auth/signup/google`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ code, redirect_uri: redirectUri }),
  })
  if (resp.status === 503) throw new SignupGatedError()
  if (!resp.ok) {
    const txt = await resp.text()
    throw new Error(`Google signup failed (${resp.status}): ${txt.slice(0, 200)}`)
  }
  const data = (await resp.json()) as SignupResponse
  state = {
    user: data.user,
    accessToken: data.access_token,
    refreshToken: data.refresh_token,
  }
  persist()
  notify()
  return data
}

export interface VerifyEmailResponse {
  verified: boolean
  already_verified: boolean
  email: string
  next_url: string | null
}

export async function verifyEmail(token: string, next?: string): Promise<VerifyEmailResponse> {
  const qs = new URLSearchParams({ token })
  if (next) qs.set('next', next)
  const resp = await fetch(`${AUTH_API}/api/v1/auth/verify-email?${qs}`)
  if (!resp.ok) {
    const txt = await resp.text()
    throw new Error(`Verify failed (${resp.status}): ${txt.slice(0, 200)}`)
  }
  return (await resp.json()) as VerifyEmailResponse
}

// ---------------------------------------------------------------------------
// Token refresh
// ---------------------------------------------------------------------------

export async function refreshAccessToken(): Promise<boolean> {
  if (EMBEDDED_FRAME) {
    const token = embeddedTokenRequester ? await embeddedTokenRequester() : null
    if (!token) return false
    state = { ...state, accessToken: token }
    notify()
    return true
  }
  if (!state.refreshToken) return false

  try {
    const resp = await fetch(`${AUTH_API}/api/v1/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: state.refreshToken }),
    })

    if (!resp.ok) {
      logout()
      return false
    }

    const data = await resp.json()
    state.accessToken = data.access_token
    state.refreshToken = data.refresh_token
    persist()
    notify()
    return true
  } catch {
    logout()
    return false
  }
}
